import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, Animated, Easing, AppState,
} from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import Colors from '../../../../constants/Colors';
import SectionHeader from '../../../../components/SectionHeader';
import { DateRange } from '../../../../redux/dateRangeSlice';
import GetMetric from '../../../../requests/Sensors/GetMetric';
import Style from '../../../../constants/Style';
import LocationSelector from '../../../../components/Occupancy/LocationSelector';
import ScrollViewStyled from '../../../../components/ScrollViewStyled';
import Switch from '../../../../components/Switch';
import MetricScoreSection from './components/MetricScoreSection';
import { AssetInstance, SensorLabel, TemplateType } from '../../../../constants/Types';
import getAssets from '../../../../requests/Assets/GetAssets';
import GetExternalEnvironmentData from '../../../../requests/Sensors/GetExternalEnvironmentData';
import { getAverageFromGraphData } from '../../../../components/TopSection';
import GetMetricHappyScoreData from '../../../../requests/Sensors/GetMetricHappyScoreData';
import AirQualityGraph, { MetricProps } from '../../../../graphs/AirQualityGraph';
import MetricValueCard from './components/MetricValueCard';
import AirQualitySafeLevelsSection from './components/AirQualitySafeLevelsSection';
import MetricSelectionButton from './components/MetricSelectionButton';
import MetricInfoCardSection from './components/MetricInfoCardSection';
import { ChartDataPoint } from '../../../../graphs/axisUtils';

interface AirQualitySectionProps {
  metrics: MetricProps[],
  title: string,
  overallScoreIndex: string,
  locationSelectionShown?: boolean,
  selectedSensor?: string,
  sensorName?: string,
  floorId?: string,
  deepLinkMetric?: number,
}

const animation = (animatedValue: Animated.Value) => Animated.sequence([
  Animated.timing(animatedValue, { duration: 0, useNativeDriver: true, toValue: 0 }),
  Animated.timing(animatedValue, {
    duration: 500, useNativeDriver: true, toValue: 1, easing: Easing.ease,
  }),
]);

const AirQualitySection = ({
  metrics, title, overallScoreIndex, locationSelectionShown, selectedSensor, sensorName,
  floorId, deepLinkMetric = null,
}: AirQualitySectionProps) => {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => (state
    .dateComparisonRange));
  const liveDateRange = useSelector((state: { liveDateRange }) => (state.liveDateRange));
  const showComparison: boolean = !!compareRange.start && !!compareRange.end;
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const routeName: string = useRoute().name;
  const [defaultShown, setDefaultShown] = useState<boolean>(false);
  const [overallScore, setOverallScore] = useState<number>(null);
  const [overallScoreCompared, setOverallScoreCompared] = useState<number>(null);
  const [selectedMetricIndex, setSelectedMetricIndex] = useState<number>(0);
  const [alternateMetricIndex, setAlternateMetricIndex] = useState<number>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(null);
  const [selectedSensorName, setSelectedSensorName] = useState<string | null>(null);
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(selectedSensor);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<ChartDataPoint[][]>(metrics.map(() => []));
  const [graphDataCompared,
    setGraphDataCompared] = useState<ChartDataPoint[][]>(metrics.map(() => []));
  const [liveValues] = useState<number[]>(metrics.map(() => null));
  const [scores] = useState<number[]>(metrics.map(() => null));
  const [scoresCompared] = useState<number[]>(metrics.map(() => null));
  const [chartSectionOpacity] = useState<Animated.Value>(new Animated.Value(1));
  const [chartOpacity] = useState<Animated.Value>(new Animated.Value(1));
  const [sectionOpacity] = useState<Animated.Value>(new Animated.Value(1));
  const [containerHeight, setContainerHeight] = useState<number>(500);
  const [sensorsShown, setSensorsShown] = useState<boolean>(false);

  const [locationAssets, setLocationAssets] = useState<string[] | null>(null);

  useEffect(() => {
    const selectedLocationId: string = selectedSpaceId ?? selectedAreaId ?? selectedFloorId;
    if (!selectedLocationId) {
      setLocationAssets(null);
      return;
    }
    getAssets({ locationId: selectedLocationId, enableSubLocationsLookUp: false }).then((res) => {
      setLocationAssets(res?.assets
        ?.filter((asset: AssetInstance) => asset.type === TemplateType.SENSOR)
        .map((asset: AssetInstance) => asset.id) ?? []);
    }).catch(() => setLocationAssets([]));
  }, [selectedBuildingId, selectedFloorId, selectedAreaId, selectedSpaceId]);

  useEffect(() => {
    setSelectedSensorId(selectedSensor);
    setSelectedSensorName(sensorName);
    setSelectedFloorId(floorId);
    if (selectedSensor) setSensorsShown(true);
  }, [selectedSensor]);

  useEffect(() => {
    if (deepLinkMetric != null) setSelectedMetricIndex(deepLinkMetric);
  }, [deepLinkMetric]);

  const fetchGraphData = (compare: boolean = false) => {
    if (defaultShown || (compare && !showComparison)) {
      setGraphDataCompared([]);
      return;
    }
    const newData = metrics.map(() => []);
    metrics?.forEach((metric: MetricProps, metricIndex: number) => {
      if (metric.index) {
        GetExternalEnvironmentData({
          metric: metric.index,
          field: metric.field,
          range: compare ? compareRange : range,
        }).then((metricData) => {
          newData[metricIndex] = metricData
            ?.map((point: { date: string, value: null | number }, index: number) => ({
              ...point, index,
            })) ?? [];
          if (compare) setGraphDataCompared([...newData]);
          else setGraphData([...newData]);
        });
      } else {
        (metric.table ? GetMetric({
          metric: metric.table,
          range: compare ? compareRange : range,
          externalId: selectedExternalId,
          deviceNumId: selectedSensorId,
          aggregationFunction: metric.aggregationFunction,
        }) : GetMetricHappyScoreData({
          metric: metric.scoreValue,
          range: compare ? compareRange : range,
        }))?.then((metricData) => {
          (metricData ?? []).forEach((e, index: number) => newData[metricIndex]?.push({
            value: (e.value ?? 0) / (metric.factor ?? 1),
            date: e.timestamp ?? e.date,
            index,
          }));
          if (compare) setGraphDataCompared([...newData]);
          else setGraphData([...newData]);
        });
      }
    });
  };

  useEffect(() => fetchGraphData(), [range, selectedExternalId, selectedSensorId, defaultShown]);
  useEffect(() => fetchGraphData(true), [
    compareRange, selectedExternalId, selectedSensorId, defaultShown]);

  const fetchLiveData = () => {
    if (!(!defaultShown && AppState.currentState === 'active'
      && (routeName?.toLowerCase().includes('internal')
        || routeName?.toLowerCase().includes('external')))) return;
    metrics.forEach((metric, index) => {
      if (metric.index) {
        GetExternalEnvironmentData({
          metric: metric.index,
          field: metric.field,
          range: liveDateRange,
        })?.then((metricData) => {
          const value: number | null = metricData ? metricData[metricData.length - 1]?.value : null;
          liveValues[index] = (typeof value === 'number' && value > -1)
            ? Math.round(10 * value) / 10 : null;
        });
      } else {
        (metric.table ? GetMetric({
          metric: metric.table,
          range: liveDateRange,
          externalId: selectedExternalId,
          deviceNumId: selectedSensorId,
          aggregationFunction: metric.aggregationFunction,
        }) : GetMetricHappyScoreData({
          metric: metric.scoreValue,
          range: liveDateRange,
        }))?.then((data) => {
          if (!(data?.length > 0)) liveValues[index] = null;
          if (data?.source) {
            liveValues[index] = Math.round(10 * (data.source / (metric.factor ?? 1))) / 10;
          } else {
            const dataPoints = data?.filter((item) => typeof item.value === 'number' && item.value !== null)
              .map((item) => item.value);
            const value: number = dataPoints ? dataPoints[dataPoints.length - 1] : null;
            liveValues[index] = value > -1 ? Math.round(10 * value) / 10 : null;
          }
        });
      }
    });
  };

  useEffect(() => {
    fetchLiveData();
  }, [defaultShown, locationAssets, selectedExternalId, selectedSensorId, liveDateRange]);

  const fetchScoreData = (isCompared: boolean = false) => {
    if (isCompared && !compareRange.end) return;
    metrics.forEach((metric, index: number) => GetMetricHappyScoreData({
      metric: metric.scoreValue,
      range: isCompared ? compareRange : range,
    })?.then((data) => {
      const average: number = getAverageFromGraphData(data);
      const newScore: number = average ? Math.min(100, Math.max(0, Math.floor(average))) : null;
      if (isCompared) scoresCompared[index] = newScore;
      else scores[index] = newScore;
    }));
  };

  const fetchOverallScore = (isComparison: boolean = false) => {
    GetMetricHappyScoreData({
      metric: overallScoreIndex,
      range: isComparison ? compareRange : range,
    }).then((data) => {
      const average: number = getAverageFromGraphData(data);
      if (isComparison) setOverallScoreCompared(average);
      else setOverallScore(average);
    });
  };

  useEffect(() => {
    fetchOverallScore(false);
    fetchScoreData(false);
  }, [range]);

  useEffect(() => {
    fetchOverallScore(true);
    fetchScoreData(true);
  }, [compareRange]);

  useEffect(animation(chartSectionOpacity).start, [selectedMetricIndex, alternateMetricIndex]);
  useEffect(animation(chartOpacity).start, [graphData, graphDataCompared]);
  useEffect(animation(sectionOpacity).start, [defaultShown]);

  const selectMetric = (index: number) => {
    if ([selectedMetricIndex, alternateMetricIndex].includes(index)) {
      setSelectedMetricIndex(index);
      setAlternateMetricIndex(null);
      return;
    }
    setAlternateMetricIndex(selectedMetricIndex);
    setSelectedMetricIndex(index);
  };

  const selectedMetric: MetricProps = metrics[selectedMetricIndex];
  const alternateMetric: MetricProps | null = metrics[alternateMetricIndex];

  return <View>
    <View style={styles.wrapper}>
      {locationSelectionShown && !defaultShown
        && <View style={[styles.locationSelector, { height: containerHeight }]}>
          <LocationSelector
            {...{
              setLocationName,
              setSelectedExternalId,
              selectedBuildingId,
              selectedFloorId,
              setSelectedFloorId,
              selectedAreaId,
              setSelectedAreaId,
              selectedSpaceId,
              setSelectedSpaceId,
              sensorsShown,
              selectedSensorId,
              setSelectedSensorId,
              setSelectedSensorName,
            }}
            hideBorders
            sensorTypesShown={Object.values(SensorLabel)}
          />
          <View style={styles.includeSensorsSwitchSection}>
            <Text style={styles.includeSensorsLabel}>{'Include sensors'}</Text>
            <Switch
              toggle={() => {
                if (sensorsShown) {
                  setSelectedSensorId(null);
                  setSelectedSensorName(null);
                }
                setSensorsShown(!sensorsShown);
              }}
              toggled={sensorsShown}
            />
          </View>
        </View>}
      <View
        style={styles.container}
        onLayout={({ nativeEvent: { layout: { height } } }) => setContainerHeight(height)}
      >
        <SectionHeader defaultShown={defaultShown}
          setDefaultShown={setDefaultShown}
          button1='INFO'
          button2='GRAPH'
          title1={`${title} parameters`}
          title2={selectedSensorName ?? locationName}
          {...{ range, compareRange, showComparison }}
          style={styles.content}
        />
        {!defaultShown ? <Animated.View style={{ opacity: sectionOpacity }}>
          <ScrollViewStyled horizontal>
            {metrics?.map((metric: MetricProps, index: number) => <MetricValueCard
              key={`${metric.title}${index}_value_card`}
              {...{ metric }}
              value={liveValues[index]}
              count={metrics.length}
            />)}
          </ScrollViewStyled>
          <View style={styles.parametersMainSection}>
            <Animated.View style={{ opacity: chartSectionOpacity }}>
              <View style={styles.row}>
                <Text style={styles.metricGraphTitle}>
                  {`${selectedMetric.fullTitle ?? selectedMetric.title}`}
                  {`${selectedMetric.unit ? ` (${selectedMetric.unit})` : ''}`}
                  {!!alternateMetric && <>
                    {' vs. '}
                    {`${alternateMetric.fullTitle ?? alternateMetric.title}`}
                    {`${alternateMetric.unit ? ` (${alternateMetric.unit})` : ''}`}
                  </>}
                </Text>
              </View>
              <Animated.View style={{ opacity: chartOpacity }}>
                <AirQualityGraph
                  {...{ showComparison }}
                  data={graphData[selectedMetricIndex]}
                  dataCompared={showComparison ? graphDataCompared[selectedMetricIndex] : []}
                  title={selectedMetric.title}
                  unit={selectedMetric.unit}
                  type={selectedMetric.type}
                  alternateData={graphData[alternateMetricIndex]}
                  alternateDataCompared={graphDataCompared[alternateMetricIndex]}
                  alternateTitle={alternateMetric?.title}
                  alternateUnit={alternateMetric?.unit}
                  alternateType={alternateMetric?.type}
                />
              </Animated.View>
              <AirQualitySafeLevelsSection {...{ selectedMetric }} />
            </Animated.View>
          </View>
          <View style={styles.metricButtonsRow}>
            {metrics?.map((item, index) => <MetricSelectionButton
              title={item.title}
              unit={item.unit}
              key={`${item.title}${index}_selection_button`}
              selected={selectedMetricIndex === index}
              alternateSelected={alternateMetricIndex === index}
              onPress={() => selectMetric(index)}
              {...{ index }}
            />)}
          </View>
        </Animated.View>
          : <Animated.View style={[styles.scoreContent, { opacity: sectionOpacity }]}>
            <MetricScoreSection
              {...{
                scores, scoresCompared, overallScore, overallScoreCompared, showComparison, metrics,
              }}
            />
            <MetricInfoCardSection {...{ metrics }} />
          </Animated.View>}
      </View>
    </View>
  </View>;
};

const styles = StyleSheet.create({
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  wrapper: {
    ...Style.row,
    backgroundColor: '#FFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite300,
    marginVertical: 24,
    overflow: 'visible',
  },
  locationSelector: {
    height: '100%',
    width: 240,
    borderEndWidth: 1,
    borderEndColor: Colors.light.hereworksBlue50,
    alignSelf: 'stretch',
  },
  container: {
    flex: 1,
    paddingTop: 18,
    paddingBottom: 27,
  },
  content: {
    marginBottom: 16,
  },
  scoreContent: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    height: 550,
  },
  metricButtonsRow: {
    flexDirection: 'row',
    marginHorizontal: 18,
    flexWrap: 'wrap',
  },
  metricGraphTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginEnd: 24,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  parametersMainSection: {
    marginVertical: 16,
    marginHorizontal: 24,
    borderBottomWidth: 1.5,
    borderBottomColor: Colors.light.hereworksWhite500,
  },
  includeSensorsSwitchSection: {
    ...Style.row,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: Colors.light.hereworksWhite100,
  },
  includeSensorsLabel: {
    fontSize: 12,
    lineHeight: 12,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
  },
});

export default AirQualitySection;
